import { getAllLevels, getLevelDomains } from './levels';
import { getAllCategories } from './categories';
import { getLevelAdminPermissions } from './admin_permissions';

// How long the cached grid content is trusted before a fresh fetch is required -- a safety net
// for changes made from another session/device (which this client's own invalidate calls can't
// see), not the primary correctness mechanism. Kept far longer than levelContentCache.js's 60s
// (30 minutes) since re-fetching on every single revisit was the actual complaint -- most
// same-session staleness is already handled by invalidateDashboardGridContent(), called by
// DashboardGrid.jsx after every category/level create/update/delete/reorder.
const CACHE_TTL_MS = 1800000;

// Single slot, not keyed -- there's only ever one dashboard grid, for whichever user is
// currently logged in. Holds { data, timestamp } once resolved, or a Promise while in flight (so
// concurrent callers -- e.g. two DashboardGrid instances mounting in quick succession -- share
// one fetch instead of issuing duplicates).
let cacheEntry = null;

/**
 * Fetch everything DashboardGrid.jsx needs to render the grid: levels, their domains,
 * categories, and level-admin permissions (unfiltered -- DashboardGrid filters to the current
 * user itself, same as it already did before this cache existed).
 */
export async function loadDashboardGridContent() {
    const [levels, levelDomains, categories, levelAdminPermissions] = await Promise.all([
        getAllLevels(),
        getLevelDomains(),
        getAllCategories(),
        getLevelAdminPermissions(),
    ]);
    return { levels, levelDomains, categories, levelAdminPermissions };
}

/**
 * Returns the cached grid content if present and still fresh, else null. Never triggers a fetch.
 */
export function getCachedDashboardGridContent() {
    if (!cacheEntry || cacheEntry instanceof Promise) return null;
    if (Date.now() - cacheEntry.timestamp > CACHE_TTL_MS) {
        cacheEntry = null;
        return null;
    }
    return cacheEntry.data;
}

/**
 * Fetches and caches the grid content if it isn't already cached-and-fresh (or already in
 * flight). Fire-and-forget friendly -- callers that don't need the result can ignore the
 * returned promise.
 */
export function prefetchDashboardGridContent() {
    if (cacheEntry instanceof Promise) return cacheEntry;
    if (cacheEntry && Date.now() - cacheEntry.timestamp <= CACHE_TTL_MS) return Promise.resolve(cacheEntry.data);

    const promise = loadDashboardGridContent().then(data => {
        cacheEntry = { data, timestamp: Date.now() };
        return data;
    }).catch(error => {
        cacheEntry = null;
        throw error;
    });
    cacheEntry = promise;
    return promise;
}

/**
 * Writes already-fetched grid content straight into the cache, skipping a redundant fetch --
 * DashboardGrid.jsx calls this once its own mount-time fetch resolves, so the *next* visit within
 * the TTL can skip the loading spinner too.
 * @param {{ levels: any[], levelDomains: any[], categories: any[], levelAdminPermissions: any[] }} data
 */
export function setCachedDashboardGridContent(data) {
    cacheEntry = { data, timestamp: Date.now() };
}

/**
 * Drops the cached grid content, if present. Call after any category/level
 * create/update/delete/reorder, so a subsequent revisit within the TTL doesn't serve back stale
 * pre-edit data.
 */
export function invalidateDashboardGridContent() {
    cacheEntry = null;
}
